import * as React from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import { cn } from '../../lib/utils';

/**
 * The block styles the design offers, and no others.
 *
 * Three named choices instead of six heading levels is the point: a product
 * description with h1 through h6 available becomes heading soup, and the
 * storefront theme only styles a couple of them anyway.
 */
const BLOCKS = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'heading', label: 'Heading' },
  { value: 'quote', label: 'Quote' },
] as const;

type BlockValue = (typeof BLOCKS)[number]['value'];

const ToolButton: React.FC<{
  label: string;
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}> = ({ label, title, active, disabled, onClick, className, children }) => (
  <button
    type="button"
    // Toolbar buttons act on a selection, so they must not take focus off the
    // text — mousedown default is what moves it.
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-pressed={active}
    title={title}
    data-testid={`rte-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className={cn(
      'inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-md px-1.5 text-xs',
      'text-muted-foreground transition-colors',
      'hover:bg-card hover:text-foreground',
      'disabled:pointer-events-none disabled:opacity-50',
      active && 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary',
      className,
    )}
  >
    {children}
  </button>
);

const Divider = () => <span aria-hidden className="mx-1 h-4 w-px bg-border" />;

export interface EditorToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

/**
 * The description editor's controls, in the design's five groups: block style ·
 * inline styles · lists · link and clean-up · undo/redo.
 *
 * These are ordinary buttons carrying the words the design draws — "B", "• List",
 * "Clear format". The previous editor drew them as TinyMCE icon buttons inside a
 * skin we could only override from the outside, which is also why the fidelity
 * check could not read a single control on this screen and eleven of them had to
 * be signed off by eye.
 */
export const EditorToolbar: React.FC<EditorToolbarProps> = ({ editor, disabled }) => {
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [href, setHref] = React.useState('');

  // Only the facts the toolbar draws, so a keystroke that changes none of them
  // does not re-render it.
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      // Any heading level reads as "Heading" — an imported h2 is a heading to
      // the person looking at it, and telling them it is a paragraph would be
      // wrong in the one place they would go to change it.
      block: (ed.isActive('blockquote')
        ? 'quote'
        : ed.isActive('heading')
          ? 'heading'
          : 'paragraph') as BlockValue,
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      underline: ed.isActive('underline'),
      strike: ed.isActive('strike'),
      bullet: ed.isActive('bulletList'),
      ordered: ed.isActive('orderedList'),
      link: ed.isActive('link'),
      canUndo: ed.can().undo(),
      canRedo: ed.can().redo(),
    }),
  });

  const setBlock = (next: BlockValue) => {
    const chain = editor.chain().focus();
    if (next === 'heading') chain.setNode('heading', { level: 3 });
    else chain.setNode('paragraph');
    if (next === 'quote' && state.block !== 'quote') chain.wrapIn('blockquote');
    if (next !== 'quote' && state.block === 'quote') chain.lift('blockquote');
    chain.run();
  };

  const openLink = () => {
    setHref(editor.getAttributes('link').href ?? '');
    setLinkOpen(true);
  };

  const applyLink = () => {
    const url = href.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    // Emptying the box and applying is how a link comes off, which is the
    // behaviour of every editor that puts the URL in a box like this one.
    if (url) chain.setLink({ href: url });
    else chain.unsetLink();
    chain.run();
    setLinkOpen(false);
  };

  return (
    <div className="relative">
      <div
        role="toolbar"
        aria-label="Formatting"
        className="flex items-center gap-0.5 border-b border-border bg-muted px-2 py-1.5"
      >
        <select
          aria-label="Block style"
          data-testid="rte-block-style"
          disabled={disabled}
          value={state.block}
          onChange={(event) => setBlock(event.target.value as BlockValue)}
          className={cn(
            'h-7 w-28 rounded-md border border-border bg-card px-1.5 text-xs text-foreground',
            'disabled:opacity-50',
          )}
        >
          {BLOCKS.map((block) => (
            <option key={block.value} value={block.value}>
              {block.label}
            </option>
          ))}
        </select>

        <Divider />

        <ToolButton
          label="Bold"
          title="Bold"
          active={state.bold}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="font-extrabold"
        >
          B
        </ToolButton>
        <ToolButton
          label="Italic"
          title="Italic"
          active={state.italic}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <i>I</i>
        </ToolButton>
        <ToolButton
          label="Underline"
          title="Underline"
          active={state.underline}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <u>U</u>
        </ToolButton>
        <ToolButton
          label="Strikethrough"
          title="Strikethrough"
          active={state.strike}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <s>S</s>
        </ToolButton>

        <Divider />

        <ToolButton
          label="Bullet list"
          title="Bullet list"
          active={state.bullet}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToolButton>
        <ToolButton
          label="Numbered list"
          title="Numbered list"
          active={state.ordered}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToolButton>

        <Divider />

        <ToolButton
          label="Link"
          title="Link"
          active={state.link || linkOpen}
          disabled={disabled}
          onClick={openLink}
        >
          Link
        </ToolButton>
        <ToolButton
          label="Clear format"
          title="Clear format"
          disabled={disabled}
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          Clear format
        </ToolButton>

        <span className="ml-auto" />

        <ToolButton
          label="Undo"
          title="Undo"
          disabled={disabled || !state.canUndo}
          onClick={() => editor.chain().focus().undo().run()}
        >
          ↺
        </ToolButton>
        <ToolButton
          label="Redo"
          title="Redo"
          disabled={disabled || !state.canRedo}
          onClick={() => editor.chain().focus().redo().run()}
        >
          ↻
        </ToolButton>
      </div>

      {/* The address, edited where the link is — a small popover under the
          control that opened it, rather than a modal over the whole page. */}
      {linkOpen && (
        <div
          data-testid="rte-link-popover"
          className="absolute left-2 top-full z-10 mt-1 w-[19rem] rounded-md border border-border bg-card p-2.5 shadow-lg"
        >
          <div className="flex items-center gap-1.5">
            <input
              autoFocus
              aria-label="Link address"
              data-testid="rte-link-input"
              placeholder="https://"
              value={href}
              onChange={(event) => setHref(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyLink();
                }
                if (event.key === 'Escape') setLinkOpen(false);
              }}
              className={cn(
                'h-8 w-full rounded-md border border-border bg-card px-2 text-xs text-foreground',
                'placeholder:text-muted-foreground focus:border-ring focus:outline-none',
              )}
            />
            <button
              type="button"
              data-testid="rte-link-apply"
              onClick={applyLink}
              className="h-8 shrink-0 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
